// src/core/hooks/useUrlPagination.ts

import { useCallback } from "react"
import { GridPaginationModel } from "@mui/x-data-grid-pro"
import { useUrlFilters, UrlFilterSchema } from "./useUrlFilters"

const PAGINATION_SCHEMA = {
  page: "number",
  pageSize: "number",
} as const satisfies UrlFilterSchema

/**
 * Keeps a DataGrid pagination model in the URL so the active page is part of
 * the shareable link. Defaults are omitted from the query string to keep URLs
 * clean (e.g. page 0 / the default page size produce no params).
 */
export function useUrlPagination(defaultPageSize: number, defaultPage = 0) {
  const { read, write } = useUrlFilters(PAGINATION_SCHEMA)
  const { page = defaultPage, pageSize = defaultPageSize } = read()

  const paginationModel: GridPaginationModel = { page, pageSize }

  const setPaginationModel = useCallback(
    (model: GridPaginationModel) => {
      write({
        page: model.page === defaultPage ? undefined : model.page,
        pageSize:
          model.pageSize === defaultPageSize ? undefined : model.pageSize,
      })
    },
    [write, defaultPage, defaultPageSize],
  )

  return { page, pageSize, paginationModel, setPaginationModel }
}
